/**
 * Auth modal experiment gallery (`AuthModal` glam layout).
 * - Columns 1–2: {@link AUTH_MODAL_VIRAL_GALLERY_URLS} (first N selfie-styles rows, filtered, shuffled).
 * - Column 3: {@link AUTH_MODAL_HEADSHOT_PROMO_URLS}.
 *
 * To drop a broken viral image without editing JSON, add its URL to `AUTH_MODAL_VIRAL_IMAGE_BLOCKLIST`.
 */

import selfieStylesData from "@/app/editor/data/selfie-styles.json";

type SelfieStyleRow = {
  id: string;
  title?: string;
  prompt: string;
  image?: string;
};

const VIRAL_PROMPT_EXCLUDE = ["couple", "parents", "two "] as const;

/** Only use the first N rows of `selfie-styles.json` (indices 0 through N - 1). */
const AUTH_MODAL_VIRAL_SELFIE_LIMIT = 100;

/**
 * Change this string only to reshuffle which of those 100 images appears where in the marquee.
 * Same key → same order across builds.
 */
export const AUTH_MODAL_VIRAL_SHUFFLE_KEY = "auth-modal-gallery-v1";

/** URLs to skip (404, etc.); keep the full viral grid otherwise. */
const AUTH_MODAL_VIRAL_IMAGE_BLOCKLIST = new Set<string>([
  // e.g. "https://photoeditor-assets.sfo3.cdn.digitaloceanspaces.com/bad-id.jpg",
]);

function stableHash(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function isViralExcluded(prompt: string): boolean {
  const lower = prompt.toLowerCase();
  return VIRAL_PROMPT_EXCLUDE.some((word) => lower.includes(word));
}

/** Same paths as `HEADSHOT_PROMO_ENTRIES` in `app/editor/tools/ai-avatars-v2.tsx`. */
export const AUTH_MODAL_HEADSHOT_PROMO_URLS: string[] = [
  "/assets/images/headshots/balzer.jpeg",
  "/assets/images/headshots/bcasual.jpeg",
  "/assets/images/headshots/blazer-2.jpeg",
  "/assets/images/headshots/blshirt.jpeg",
  "/assets/images/headshots/bshirt.jpeg",
  "/assets/images/headshots/suittie.jpeg",
  "/assets/images/headshots/bturtle.jpeg",
  "/assets/images/headshots/white_shirt.jpeg",
];

export const AUTH_MODAL_VIRAL_GALLERY_URLS: string[] = (() => {
  const slice = (selfieStylesData as SelfieStyleRow[]).slice(0, AUTH_MODAL_VIRAL_SELFIE_LIMIT);
  const picked: SelfieStyleRow[] = [];
  const seenId = new Set<string>();
  for (const s of slice) {
    if (!s.title?.trim() || !s.image || isViralExcluded(s.prompt)) continue;
    if (AUTH_MODAL_VIRAL_IMAGE_BLOCKLIST.has(s.image)) continue;
    if (seenId.has(s.id)) continue;
    seenId.add(s.id);
    picked.push(s);
  }
  const sorted = [...picked].sort(
    (a, b) =>
      stableHash(`${AUTH_MODAL_VIRAL_SHUFFLE_KEY}:${a.id}`) -
      stableHash(`${AUTH_MODAL_VIRAL_SHUFFLE_KEY}:${b.id}`),
  );
  return sorted.map((s) => s.image!);
})();
